import type {
  BufferedAction,
  Fighter,
  FighterId,
  FighterState,
  GameStatus,
  InputFrame,
  LedgeId,
  Projectile,
} from "./game-types";

export const REPLAY_VERSION = 4;

export interface VectorSnapshot {
  x: number;
  y: number;
}

export type FighterSnapshot = Omit<
  Fighter,
  "config" | "position" | "previousPosition" | "velocity"
> & {
  position: VectorSnapshot;
  previousPosition: VectorSnapshot;
  velocity: VectorSnapshot;
  stateAgeFrames: number;
};

export type ProjectileSnapshot = Omit<
  Projectile,
  "position" | "velocity"
> & {
  position: VectorSnapshot;
  velocity: VectorSnapshot;
};

export interface SimulationSnapshot {
  playerId: FighterId;
  cpuId: FighterId;
  status: GameStatus;
  timeRemaining: number;
  countdown: number;
  hitStop: number;
  winner: FighterId | null;
  cpuEnabled: boolean;
  frameNumber: number;
  projectileId: number;
  actionInstanceId: number;
  previousCountdownBeat: number;
  cpuThinkTimer: number;
  cpuAttackCooldown: number;
  cpuSeed: number;
  fighters: FighterSnapshot[];
  projectiles: ProjectileSnapshot[];
}

export interface ReplayFrame {
  input: InputFrame;
  pausedStep: boolean;
}

export interface SimulationReplay {
  version: typeof REPLAY_VERSION;
  label: string;
  initialState: SimulationSnapshot;
  frames: ReplayFrame[];
  finalHash?: string;
}

export interface FighterDiagnostics {
  id: FighterId;
  state: FighterState;
  stateAgeFrames: number;
  motionOwner:
    | "respawn"
    | "captured"
    | "grab-hold"
    | "hit-reaction"
    | "shield"
    | "attack-action"
    | "special-action"
    | "grab-action"
    | "ledge-hang"
    | "ledge-action"
    | "ground-movement"
    | "air-movement";
  grounded: boolean;
  groundPlatform: string | null;
  position: VectorSnapshot;
  velocity: VectorSnapshot;
  actionFramesRemaining: number;
  hitstunFramesRemaining: number;
  invulnerabilityFramesRemaining: number;
  bufferedAction: BufferedAction | null;
  bufferedActionFrames: number;
  comboCount: number;
  comboRecoveryFrames: number;
  comboDamageScale: number;
  comboKnockbackScale: number;
  comboHitstunScale: number;
  jumpsRemaining: number;
  ledgeId: LedgeId | null;
  ledgeHangFramesRemaining: number;
  ledgeGrabCooldownFrames: number;
  holding: FighterId | null;
  grabbedBy: FighterId | null;
}

export function createReplay(
  initialState: SimulationSnapshot,
  label = "combat-capture",
): SimulationReplay {
  return {
    version: REPLAY_VERSION,
    label,
    initialState: structuredClone(initialState),
    frames: [],
  };
}

export function recordReplayFrame(
  replay: SimulationReplay,
  input: InputFrame,
  pausedStep = false,
): void {
  replay.frames.push({ input: { ...input }, pausedStep });
}

export function cloneReplay(replay: SimulationReplay): SimulationReplay {
  return structuredClone(replay);
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  const object = value as Record<string, unknown>;
  return `{${Object.keys(object)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(object[key])}`)
    .join(",")}}`;
}

export function hashDeterministicState(value: unknown): string {
  const serialized = stableStringify(value);
  let hash = 0x811c9dc5;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}
